import { Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  collection,
  doc,
  DocumentData,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  QueryDocumentSnapshot,
  QuerySnapshot
} from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthStateService } from "@core/auth-state.service"; // [FIX] Import AuthStateService to gate Firestore listeners on custom token readiness
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { combineLatest, EMPTY, firstValueFrom, from, Observable, of, ReplaySubject, throwError } from "rxjs";
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take, tap } from "rxjs/operators";
import { CommonService } from "src/app/services/common/common.service";
import { LoggingService } from "src/app/services/logging.service";
import { UserManagementService } from "src/app/services/user-management.service";

@Injectable({
  providedIn: "root",
})
export class HomeRepositoryService {
  slideToCharity$ = new ReplaySubject<boolean>(1);
  // [FIX] Declared without initializer — assigned in constructor after DI is complete.
  // Previously initialized as a class field, which runs before the constructor,
  // meaning authStateService was not yet injected when getHomePageData() was called.
  homeCardsSignalData: ReturnType<typeof toSignal>;
  homeScrollCardsSignalData: ReturnType<typeof toSignal>;
  toolTipText$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return from(Promise.resolve([])); // Return empty observable if offline

      // Modular Firestore API: Reference the collection and fetch snapshot changes
      const collectionRef = collection(this.firestore, "/Global_lookup/onboarding/tooltips");
      return from(getDocs(collectionRef)); // Ensures proper typing for Firestore QuerySnapshot
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    map((querySnapshot: QuerySnapshot<DocumentData>) => {
      // Pass the array of QueryDocumentSnapshot directly to snapShotData
      return this.snapShotData(querySnapshot.docs);
    }),
    shareReplay(1)
  );
  async productToolTip() {
    try {
      // Modular Firestore API: Reference the document
      const documentRef = doc(
        this.firestore,
        "Global_lookup/onboarding/tooltips/product"
      );

      // Fetch the document snapshot
      const ref = await getDoc(documentRef);

      if (ref?.exists()) {
        return { success: true, data: ref.data() };
      } else {
        return { success: false, code: 'DOCUMENT_NOT_FOUND', message: 'Document does not exist' };
      }
    } catch (error) {
      return { success: false, code: 'FETCH_ERROR', message: 'Error fetching product tooltip: ' + error.message };
    }
  }
  private whatsonBlogs$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY; // Handle offline gracefully

      // [FIX] Gate on customTokenReady$ before opening the Firestore listener.
      // Prevents "permission-denied" race condition on iOS Safari (iOS 18.7+)
      // where onSnapshot fires before the Firebase custom token is available.
      return this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => {
          // Modular Firestore API: Reference the document
          const documentRef = doc(this.firestore, `/home_module/home_blog`);
          // Use `onSnapshot` for real-time updates
          return new Observable((observer) => {
            const unsubscribe = onSnapshot(
              documentRef,
              (docSnapshot) => observer.next({ payload: { data: () => docSnapshot.data() } }), // Adapts structure to original logic
              (error) => observer.error(error)
            );
            return () => unsubscribe();
          }).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            // Handle permission-denied errors gracefully
            catchError((error: unknown) => {
              if (error instanceof Error && error.message.includes('permission')) {
                this.loggingService.logError('[whatsonBlogs] Permission denied, returning empty blogs', error);
                return of({ payload: { data: () => ({ content: [] }) } });
              }
              return throwError(() => error);
            })
          );
        }) // end customTokenReady$ switchMap
      ); // end customTokenReady$ pipe
    }),  // end isOnline switchMap
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1)
  ).pipe(
    map((res: any) => res?.payload?.data()) // Extract document data
  );

  private whatsonNews$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY; // Handle offline case gracefully

      // [FIX] Gate on customTokenReady$ before opening the Firestore listener.
      // Prevents "permission-denied" race condition on iOS Safari (iOS 18.7+)
      // where onSnapshot fires before the Firebase custom token is available.
      return this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => {
          // Modular Firestore API: Reference the document
          const documentRef = doc(this.firestore, `/home_module/home_news`);

          // Use `onSnapshot` for real-time updates wrapped in an Observable
          return new Observable((observer) => {
            const unsubscribe = onSnapshot(
              documentRef,
              (docSnapshot) => observer.next({ payload: { data: () => docSnapshot.data() } }), // Adapt payload structure
              (error) => observer.error(error)
            );
            return () => unsubscribe();
          }).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            // Handle permission-denied errors gracefully
            catchError((error: unknown) => {
              if (error instanceof Error && error.message.includes('permission')) {
                this.loggingService.logError('[whatsonNews] Permission denied, returning empty news', error);
                return of({ payload: { data: () => ({ content: [] }) } });
              }
              return throwError(() => error);
            })
          );
        }) // end customTokenReady$ switchMap
      ); // end customTokenReady$ pipe
    }),  // end isOnline switchMap
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    map((res: any) => res?.payload?.data()) // Extract document data
  );

  whatsOnData$ = combineLatest(this.whatsonBlogs$, this.whatsonNews$);

  updateJackpotData$: Observable<void> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (isOnline) {
        // [FIX] Gate on customTokenReady$ before opening the Firestore listener.
        // Prevents "permission-denied" race condition on iOS Safari (iOS 18.7+)
        // where onSnapshot fires before the Firebase custom token is available.
        return this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => {
            // Modular Firestore API: Reference the collection and set up real-time snapshot listening
            const collectionRef = collection(this.firestore, "/jackpot/");
            return new Observable((observer) => {
              const unsubscribe = onSnapshot(
                collectionRef,
                (querySnapshot) => {
                  const documents = querySnapshot.docs.map((doc) => doc.data());
                  observer.next(documents);
                },
                (error) => observer.error(error)
              );
              return () => unsubscribe();
            }).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError,
              // Handle permission-denied errors gracefully
              catchError((error: unknown) => {
                if (error instanceof Error && error.message.includes('permission')) {
                  this.loggingService.logError('[updateJackpotData] Permission denied, skipping jackpot update', error);
                  return of([]);
                }
                return throwError(() => error);
              })
            );
          }) // end customTokenReady$ switchMap
        ); // end customTokenReady$ pipe
      } else {
        return EMPTY; // Handle offline case gracefully
      }
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1)
  ).pipe(
    tap((globalData: any) => {
      if (globalData) {
        this.authService.getUid$("HomeRepositoryService").pipe(
          switchMap((userId) => {
            if (!userId) return EMPTY;
            const req = { userId: userId, page: "home", method: "createJackpotCard" };
            return this.authService.updateMainPages(req);
          }),
          catchError((err) => {
            this.loggingService.logError('[HomeRepositoryService] createJackpotCard error:', err);
            return EMPTY;
          })
        ).subscribe();
      }
    })
  );

  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private authService: AuthService,
    private authStateService: AuthStateService, // [FIX] Injected to gate Firestore listeners on custom token readiness
    private userManagementService: UserManagementService,
    private commonService: CommonService,
    private loggingService: LoggingService
  ) {
    // [FIX] Assigned here so all injected services (especially authStateService) are
    // fully available before getHomePageData() opens any Firestore listeners.
    this.homeCardsSignalData = toSignal(this.getHomePageData());
    this.homeScrollCardsSignalData = toSignal(this.getHomeScrollPageData());
  }

  public newMemberChecklistCard(userId: string): void {
    // V2
    this.authService.getV2CloudFunction("assignnewmemberchecklistv2", userId)?.pipe(
      catchError((err) => {
        this.loggingService.logError('[HomeRepositoryService] assignnewmemberchecklistv2 error:', err);
        return EMPTY;
      })
    ).subscribe();
    // V1
    // const checklistHomeCard = this.angularFireFunctions.httpsCallable("assignNewMemberCheckList");
    // checklistHomeCard(userId)?.subscribe();
  }

  updateCardViewCount(limbMsgId) {
    this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return EMPTY;
          return this.authService.getUid$("HomeRepositoryService").pipe(
            switchMap((userId) => {
              // Modular Firestore API: Reference the document
              const documentRef = doc(
                this.firestore,
                `/users/${userId}/home_card_views/${limbMsgId}`
              );

              // Defense-in-depth gate (LOTTOSOCIAL-PWA-1MEE)
              return this.authStateService.customTokenReady$.pipe(
                take(1),
                switchMap(() =>
                  from(getDoc(documentRef)).pipe(
                    map((docSnapshot) => ({
                      exists: docSnapshot.exists(),
                      data: docSnapshot.data(),
                    }))
                  )
                )
              );
            }));
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .subscribe(
        (cardView: any) => {
          if (!cardView?.data || (cardView?.data && !cardView?.data.view_count)) {
            this.updateViewCountCall({ view_count: 1 }, limbMsgId).subscribe();
          } else {
            this.updateViewCountCall({ view_count: cardView?.data.view_count + 1 }, limbMsgId).subscribe();
          }
        },
        (err) => {
          return err;
        }
      );
  }

  //used to create/update the count and last visited index of user home cards in the firebase doc
  async updateCountNode(count: number, lastVisitedIndex: number): Promise<void> {
    try {
      const userId = await firstValueFrom(
        this.authService.getUid$('HomeRepositoryService')
      );

      if (!userId || count === undefined || lastVisitedIndex === undefined) {
        return;
      }

      const path_id = 'xHF2jTstQRYtSIIYHfiT';

      // First update
      await firstValueFrom(
        this.authService.getV2CloudFunction('updatefirestoredoc', {
          path_id,
          dv: { docId: count },
          doc_data: { value: count },
          method: 'update',
        })
      );

      // Second update (runs only after first completes)
      await firstValueFrom(
        this.authService.getV2CloudFunction('updatefirestoredoc', {
          path_id,
          dv: { docId: lastVisitedIndex },
          doc_data: { value: lastVisitedIndex },
          method: 'update',
        })
      );

    } catch (err) {
      this.loggingService.logError('Error updating count node', err);
    }
  }

  //used to add the viewed card id in the home page doc
  updateCardViewedNode(cardId: string) {
    return this.authService.getUid$("HomeRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId || !cardId) {
          return EMPTY;
        }

        const homeDocPath = `users/${userId}/main_pages/home`;

        return from(getDoc(doc(this.firestore, homeDocPath))).pipe(
          switchMap((snapshot) => {
            const homeData = snapshot.data() as { home_card_viewed?: string[] };

            if (!snapshot.exists()) {
              // Document doesn't exist → create with first cardId
              return this.authService.getV2CloudFunction("updatefirestoredoc", {
                path_id: homeDocPath,
                doc_data: { home_card_viewed: [cardId] },
                method: "update",
              }).pipe(
                catchError((error) => {
                  this.loggingService.logError('Error creating home_card_viewed array', error);
                  return EMPTY;
                })
              );
            } else {
              // Document exists → append cardId to home_card_viewed
              const updatedHomeCardViewed = homeData?.home_card_viewed || [];
              updatedHomeCardViewed.push(cardId);

              return this.authService.getV2CloudFunction("updatefirestoredoc", {
                path_id: "RIQYP3mmT5KYHmOq6uWJ",
                doc_data: { home_card_viewed: updatedHomeCardViewed },
                method: "create",
              }).pipe(
                catchError(() => {
                  return EMPTY;
                })
              );
            }
          }),
          catchError((error) => {
            this.loggingService.logError('Error fetching home doc:', error);
            return EMPTY;
          })
        );
      })
    );
  }

  // Used to get count and last visited index of user home cards
  getHomeCardCount(): Observable<Data[]> {
    return this.authService.getUid$("HomeRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return EMPTY; // Return empty observable if userId is not found

        // Modular Firestore API: Reference the collection
        const homeCardCountRef = collection(this.firestore, `users/${userId}/homeCardCount`);

        // Fetch data using getDocs
        return from(getDocs(homeCardCountRef)).pipe(
          map((querySnapshot) => {
            if (querySnapshot.empty) return [];
            // Map documents to array of data
            return querySnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
          }),
          catchError((error) => {
            this.loggingService.logError('Error fetching homeCardCount data', error);
            return of([]); // Return empty array on error
          })
        );
      }));
  }

  updateViewCountCall(updateInfoObj: any, limbMsgId: string) {
    return this.authService.getUid$("HomeRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) {
          this.loggingService.logError('updateViewCountCall: Missing userId');
          return EMPTY;
        }

        const path_id = `8xQI2C9mcLmLsgwriJj9`;
        const doc_data = updateInfoObj; // whatever fields you want to update
        const dv = { docId: userId, limbMsgId };
        const method = "update";

        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).pipe(
          take(1),
        );
      })
    );
  }


  setCheckWinningCardHidden(limbMsgId) {
    return this.authService.getUid$("HomeRepositoryService").pipe(
      switchMap((userId) => {
        if (userId == null) {
          return EMPTY;
        }
        const path_id = "i6Y4dDCDpxx6moWivjMh";
        const doc_data = { hide: true };
        const dv = { docId: limbMsgId };
        const method = "update";

        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).subscribe({
          error: (error) => {
            this.loggingService.logError('Error updating Firestore document:', error);
            return error;
          }
        });
        return of(userId);
      }));
  }

  setRoundCardHidden(limbMsgId) {
    return this.authService.getUid$("HomeRepositoryService").pipe(
      switchMap((userId) => {
        if (userId == null) {
          return EMPTY;
        }
        const path_id = "i6Y4dDCDpxx6moWivjMh";
        const doc_data = { hide: true };
        const dv = { docId: limbMsgId };
        const method = "update";

        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).subscribe({
          error: (error) => {
            this.loggingService.logError('Error updating Firestore document:', error);
            return error;
          }
        });
        return of(userId);
      }));
  }

  updateCharityData(i: number, charityDoc: any) {

    this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of(null);

          let docRef: any;

          // 🧩 Ensure we have a proper Firestore DocumentReference
          if (charityDoc?.path && typeof charityDoc.path === "string") {
            docRef = doc(this.firestore, charityDoc.path);
          } else if (typeof charityDoc === "string") {
            docRef = doc(this.firestore, charityDoc);
          } else {
            return of(null);
          }

          // ✅ Now call getDoc safely
          return getDoc(docRef).then((docSnapshot) => ({
            exists: docSnapshot.exists(),
            data: docSnapshot.data(),
            path: docRef.path,
          }));
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .subscribe((r: any) => {
        if (!r?.data) return;

        const d = r.data;

        // Ensure content exists and is an array
        if (!Array.isArray(d.content)) {
          d.content = [];
        }

        const otherIndex = i === 0 ? 1 : 0;
        const otherVoteCount = d.content[otherIndex]?.vote_count || 0;

        const currentVoteCount = d.content[i].vote_count;
        const totalVotes = currentVoteCount + otherVoteCount;

        d.content[i].charity_percentage = (currentVoteCount / totalVotes) * 100;
        d.content[otherIndex] = d.content[otherIndex] || {};
        d.content[otherIndex].charity_percentage =
          100 - d.content[i].charity_percentage;

        const update_doc_data = { content: d.content };
        const updateMethod = "update";
        const incrementPath = `content.${i}.vote_count`;
        // Update Firestore document via Cloud Function
        const doc_id =
          r.path?.substring(r.path.lastIndexOf("/") + 1) || null;
        const path_id = "Y1i8GyZI3GbHqCwnuVoj";
        const doc_data = { [incrementPath]: { incrementBy: 1 } };
        const dv = { docId: doc_id };
        const method = "increment";

        const payload = JSON.parse(
          JSON.stringify({ path_id, doc_data, dv, method })
        );
        const updatePayload = JSON.parse(
          JSON.stringify({ path_id, doc_data: update_doc_data, dv, method: updateMethod })
        );

        this.authService.getV2CloudFunction("updatefirestoredoc", updatePayload).subscribe({
          error: (error) => {
            this.loggingService.logError('Error updating charity vote percentage', error);
          },
        });

        setTimeout(() => {
          this.authService.getV2CloudFunction("updatefirestoredoc", payload).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            catchError((error: unknown) => {
              if (error instanceof Error && error.message.includes('permission')) {
                this.loggingService.logError(
                  '[getCharityPercentage] Permission denied, returning default charity split',
                  error
                );

                return of({
                  payload: {
                    data: () => ({
                      content: [
                        { charity_percentage: 50 },
                        { charity_percentage: 50 }
                      ]
                    })
                  }
                });
              }
              return throwError(() => error);
            })
          ).subscribe({
            error: (error) => {
              this.loggingService.logError('Error updating charity vote', error);
            },
          });
        }, 2000)

        this.authService
          .getUid$("HomeRepositoryService")
          .pipe(
            switchMap((userId) => {
              if (!userId) return of(null);
              const req = {
                userId,
                page: "home",
                method: "createCharityCard",
              };
              this.authService.updateMainPageSubscription =
                this.authService.updateMainPages(req).subscribe({
                  error: (err) => this.loggingService.logError('[HomeRepositoryService] updateMainPages (createCharityCard) error:', err),
                });
              return of(userId);
            }),
            catchError((err) => {
              this.loggingService.logError('[HomeRepositoryService] createCharityCard error:', err);
              return EMPTY;
            })
          )
          .subscribe();
      });
  }


  getCharityPercentage(charityDoc: DocumentReference): Observable<Data> {
    return this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return EMPTY;

          // Modular Firestore API: Reference the document for snapshot listening
          return new Observable((observer) => {
            const unsubscribe = onSnapshot(
              charityDoc,
              (docSnapshot) => observer.next({ payload: { data: () => docSnapshot.data() } }),
              (error) => observer.error(error)
            );
            return () => unsubscribe();
          }).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            // Handle permission-denied errors gracefully with default charity split
            catchError((error: unknown) => {
              if (error instanceof Error && error.message.includes('permission')) {
                this.loggingService.logError('[getCharityPercentage] Permission denied, returning default charity split', error);
                // Return default 50/50 charity split
                return of({
                  payload: {
                    data: () => ({
                      content: [{ charity_percentage: 50 }, { charity_percentage: 50 }]
                    })
                  }
                });
              }
              return throwError(() => error);
            })
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(map((res: any) => res?.payload?.data()));
  }

  setBGCardHidden(limbMsgId: string) {
    return this.authService.getUid$("HomeRepositoryService").pipe(
      switchMap((userId) => {
        if (userId == null) return EMPTY;

        const path_id = "i6Y4dDCDpxx6moWivjMh";
        const doc_data = { hide: true };
        const dv = { docId: limbMsgId };
        const method = "update";

        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).pipe(
          // Handle permission-denied errors gracefully
          catchError((error: unknown) => {
            if (error instanceof Error && error.message.includes('permission')) {
              this.loggingService.logError(
                '[setBGCardHidden] Permission denied updating Firestore document',
                error
              );
              return of(null);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private snapShotData(docs: QueryDocumentSnapshot<DocumentData>[]): any[] {
    return docs?.map((doc) => {
      const data = doc.data() as any;
      const id = doc.id;
      return { id, ...data };
    });
  }

  // To get complete home cards data from 'home-page' firebase document
  getHomePageData(): Observable<Data> {
    return this.userManagementService.userRequestForHome$.pipe(
      switchMap((user: Data) => {
        if (!user) {
          return of(null); // Return null if not authenticated
        }

        return this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (!isOnline) return EMPTY; // Return null if offline

            // [FIX] Gate on customTokenReady$ before opening the Firestore listener.
            // Prevents "permission-denied" race condition on iOS Safari (iOS 18.7+)
            // where onSnapshot fires before the Firebase custom token is available.
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => {
                // Modular Firestore API: Reference the document for snapshot listening
                const documentRef = doc(this.firestore, `users/${user?.uid}/main_pages/home`);

                return new Observable((observer) => {
                  const unsubscribe = onSnapshot(
                    documentRef,
                    (docSnapshot) => {
                      const data = docSnapshot.data();
                      observer.next(data);
                    },
                    (error) => observer.error(error)
                  );
                  return () => unsubscribe();
                }).pipe(
                  distinctUntilChanged((prev, curr) => {
                    // Skip only when both home_card and springboard_widgets are unchanged
                    if (!prev && !curr) return true;
                    if (!prev || !curr) return false;
                    const prevData = prev as Data;
                    const currData = curr as Data;
                    return (
                      prevData?.home_card === currData?.home_card &&
                      prevData?.springboard_widgets === currData?.springboard_widgets
                    );
                  }),
                  this.catchService.retryWhen,
                  this.catchService.catchOfflineError,
                  // Handle permission-denied errors gracefully for home page data
                  catchError((error: unknown) => {
                    if (error instanceof Error && error.message.includes('permission')) {
                      this.loggingService.logError('[getHomePageData] Permission denied fetching home page data', error);
                      return of(null);
                    }
                    return throwError(() => error);
                  }),
                  shareReplay(1) // Cache the latest data for efficiency
                );
              }) // end customTokenReady$ switchMap
            ); // end customTokenReady$ pipe
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      })
    );
  }

  getHomeScrollPageData(): Observable<Data | null> {
    return this.userManagementService.userRequestForHome$.pipe(
      switchMap((user: Data) => {
        if (!user?.uid || typeof user.uid !== 'string' || !user.uid.trim()) {
          return of(null);
        }

        const userId = user.uid.trim();

        return this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (!isOnline) return of(null); // emit null so component exits loading state

            // [FIX] Gate on customTokenReady$ before opening the Firestore listener.
            // Prevents "permission-denied" race condition on iOS Safari (iOS 18.7+)
            // where onSnapshot fires before the Firebase custom token is available.
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => {
                // Modular Firestore API: Reference the document for snapshot listening
                const documentRef = doc(this.firestore, `users/${userId}/main_pages/home_scroll`);

                return new Observable((observer) => {
                  const unsubscribe = onSnapshot(
                    documentRef,
                    (docSnapshot) => {
                      const data = docSnapshot.data();
                      observer.next(data);
                    },
                    (error) => observer.error(error)
                  );
                  return () => unsubscribe();
                }).pipe(
                  distinctUntilChanged((prev, curr) => {
                    // Skip only when both carousel and springboard_widgets are unchanged
                    if (!prev && !curr) return true;
                    if (!prev || !curr) return false;
                    const prevData = prev as Data;
                    const currData = curr as Data;
                    return (
                      prevData?.home_carousel_card === currData?.home_carousel_card &&
                      prevData?.springboard_widgets === currData?.springboard_widgets
                    );
                  }),
                  this.catchService.retryWhen,
                  this.catchService.catchOfflineError,
                  catchError((error: unknown) => {
                    if (error instanceof Error && error.message.includes('permission')) {
                      this.loggingService.logError('[getHomeScrollPageData] Permission denied fetching home scroll page data', error);
                      return of(null);
                    }
                    return throwError(() => error);
                  }),
                  shareReplay(1)
                );
              })
            );
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      })
    );
  }

  updateHomeCardData$: Observable<void> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (isOnline) {
        // [FIX] Gate on customTokenReady$ before opening the Firestore listener.
        // Prevents "permission-denied" race condition on iOS Safari (iOS 18.7+)
        // where onSnapshot fires before the Firebase custom token is available.
        return this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => {
            // Modular Firestore API: Reference the collection
            const collectionRef = collection(
              this.firestore,
              "/Global_lookup/user_messages_templates/home_card/"
            );

            // Use `onSnapshot` to listen for real-time changes
            return new Observable((observer) => {
              const unsubscribe = onSnapshot(
                collectionRef,
                (querySnapshot) => {
                  const documents = querySnapshot.docs.map((doc) => doc.data());
                  observer.next(documents);
                },
                (error) => observer.error(error)
              );
              return () => unsubscribe();
            }).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError,
              // Handle permission-denied errors gracefully
              catchError((error: unknown) => {
                if (error instanceof Error && error.message.includes('permission')) {
                  this.loggingService.logError('[updateHomeCardData] Permission denied, skipping home card data update', error);
                  return of([]);
                }
                return throwError(() => error);
              })
            );
          }) // end customTokenReady$ switchMap
        ); // end customTokenReady$ pipe
      } else {
        return EMPTY; // Return EMPTY when offline
      }
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1)
  ).pipe(
    tap((globalData: any) => {
      if (globalData) {
        this.commonService.getUid("HomeRepositoryService").pipe(take(1)).subscribe((userId) => {
          if (!userId) {
            return;
          }

          const req = {
            userId: userId,
            page: "home",
            method: "createOrUpdateHomePage",
          };

          this.authService.updateMainPageSubscription = this.authService.updateMainPages(req).subscribe({
            error: (err) => { return err },
          });
        });
      }
    })
  );

}
